/**
 * 账目 —— 仅 OWNER。
 * 不做图表：几个大数字加一张欠款表就够了。图表是给分析师的，不是给老板的。
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, ChevronLeft, ChevronRight, Download, MessageCircle, Phone, Wallet } from 'lucide-react'
import { api, type Me } from '../lib/api'
import { CASE_TYPE_SHORT, date as fmtDate, rm } from '../lib/format'
import { Button, Card, Chip, EmptyState, Skeleton, cx } from '../components/ui'

interface Summary {
  income: { total: number; byMethod: Record<string, number> }
  previousPeriodTotal: number
  /** 这段时间实际进帐多少（可能含以前月份的单的尾款）—— 对账用 */
  cashReceived: number
  outstanding: { total: number; caseCount: number }
}

interface OutstandingRow {
  caseId: string
  caseNo: string
  type: string
  customer: { id: string; fullName: string; phoneE164: string }
  deliveredAt: string | null
  finalAmount: number
  paidAmount: number
  balanceAmount: number
  overdueDays: number
}

export function FinancePage({ me }: { me: Me }) {
  const navigate = useNavigate()
  const [range, setRange] = useState<'today' | 'week' | 'month'>('month')
  /**
   * 看的是哪个月：0 = 本月，-1 = 上个月，依此类推。
   *
   * 后端一直支持 range=custom&from&to，只是前端没给入口 ——
   * 结果就是「只看得到今天和本月」，上个月做了多少生意完全查不到。
   */
  const [monthBack, setMonthBack] = useState(0)

  const shown = new Date()
  shown.setDate(1)
  shown.setMonth(shown.getMonth() - monthBack)
  const shownYear = shown.getFullYear()
  const shownMonth = shown.getMonth() + 1
  const pad = (n: number) => String(n).padStart(2, '0')
  const lastDay = new Date(shownYear, shownMonth, 0).getDate()
  const from = `${shownYear}-${pad(shownMonth)}-01`
  const to = `${shownYear}-${pad(shownMonth)}-${pad(lastDay)}`

  // 看的不是本月时才走 custom —— 本月继续用 range=month，省得两条路算出不同结果
  const useCustom = range === 'month' && monthBack > 0
  const qs = useCustom ? `range=custom&from=${from}&to=${to}` : `range=${range}`

  if (me.user.role !== 'OWNER') {
    return (
      <Card>
        <EmptyState
          icon={<Wallet size={44} />}
          title="这个页面需要老板权限"
          action={
            <Button variant="primary" size="lg" onClick={() => navigate('/')}>
              返回今天
            </Button>
          }
        />
      </Card>
    )
  }

  const summary = useQuery({
    queryKey: ['finance', 'summary', qs],
    queryFn: () => api.get<Summary>(`/api/finance/summary?${qs}`),
    placeholderData: (p) => p,
  })

  const outstanding = useQuery({
    queryKey: ['finance', 'outstanding'],
    queryFn: () => api.get<{ items: OutstandingRow[] }>('/api/finance/outstanding'),
  })

  const s = summary.data
  const cash = s?.income.byMethod.CASH ?? 0
  const duitnow = s?.income.byMethod.DUITNOW ?? 0
  const pct = (v: number) => (s && s.income.total > 0 ? Math.round((v / s.income.total) * 100) : 0)

  return (
    <div className="mx-auto max-w-[1120px]">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="flex-1 text-[28px] font-bold">账目</h1>
        <div className="flex gap-2">
          {(
            [
              ['today', '今天'],
              ['week', '本周'],
              ['month', '本月'],
            ] as const
          ).map(([k, label]) => (
            <Chip
              key={k}
              active={range === k}
              onClick={() => {
                setRange(k)
                if (k !== 'month') setMonthBack(0)
              }}
            >
              {label}
            </Chip>
          ))}
        </div>
        <Button
          icon={<Download size={18} />}
          onClick={() =>
            window.open(
              // 导出跟着屏幕上看的范围走 —— 看着 9 月却导出全部，等于导错
              useCustom ? `/api/finance/export?from=${from}&to=${to}` : '/api/finance/export',
              '_blank',
            )
          }
        >
          导出 CSV
        </Button>
      </div>

      {range === 'month' && (
        <div className="mb-4 flex items-center gap-2">
          <Button
            size="lg"
            aria-label="上个月"
            icon={<ChevronLeft size={20} />}
            onClick={() => setMonthBack((n) => n + 1)}
          />
          <span className="tnum min-w-36 text-center text-[19px] font-semibold">
            {shownYear} 年 {shownMonth} 月
            {monthBack === 0 && <span className="ml-2 text-[15px] font-normal text-ink-3">本月</span>}
          </span>
          <Button
            size="lg"
            aria-label="下个月"
            icon={<ChevronRight size={20} />}
            disabled={monthBack === 0}
            onClick={() => setMonthBack((n) => Math.max(0, n - 1))}
          />
          {monthBack > 0 && (
            <Button size="sm" onClick={() => setMonthBack(0)}>
              回到本月
            </Button>
          )}
        </div>
      )}

      {s && s.cashReceived !== s.income.total && (
        <p className="mb-4 rounded-md border border-line bg-sunken px-4 py-3 text-[16px] text-ink-2">
          <strong>业绩按单子属于哪个月算</strong>，不是按哪天收到钱。
          9 月的单在 10 月收到尾款，这笔算 9 月的业绩。
          这段时间实际进帐 <strong className="tnum">{rm(s.cashReceived)}</strong>
          （里面可能含以前月份的单）。
        </p>
      )}

      {summary.isLoading ? (
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : (
        s && (
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label={
                range === 'today'
                  ? '今天开的单'
                  : range === 'week'
                    ? '本周开的单'
                    : monthBack === 0
                      ? '本月业绩'
                      : `${shownMonth} 月业绩`
              }
              value={rm(s.income.total)}
              sub={
                // 实收和业绩不一样时才提 —— 一样的时候说它只是噪音。
                // 不提的话，10 月业绩显示 0 会让人以为数据没了。
                s.cashReceived !== s.income.total
                  ? `上期 ${rm(s.previousPeriodTotal)} · 本期实收 ${rm(s.cashReceived)}`
                  : `上期 ${rm(s.previousPeriodTotal)}`
              }
            />
            <StatCard label="现金" value={rm(cash)} sub={`${pct(cash)}%`} />
            <StatCard label="DuitNow" value={rm(duitnow)} sub={`${pct(duitnow)}%`} />
            <StatCard
              label="未收款"
              value={rm(s.outstanding.total)}
              sub={`${s.outstanding.caseCount} 单`}
              tone="warn"
            />
          </div>
        )
      )}

      <Card title="欠款清单" bodyClassName="p-0! sm:p-0!">
        {outstanding.isLoading ? (
          <div className="p-5">
            <Skeleton className="h-32" />
          </div>
        ) : (outstanding.data?.items.length ?? 0) === 0 ? (
          <div className="flex items-center justify-center gap-2 px-5 py-8 text-[17px] text-ok">
            <CheckCircle2 size={22} />
            没有欠款，全部结清了
          </div>
        ) : (
          <ul>
            {outstanding.data!.items.map((r) => (
              <li
                key={r.caseId}
                className={cx(
                  'border-b border-line px-5 py-4 last:border-0 sm:px-6',
                  // 逾期超过 30 天整行淡红底
                  r.overdueDays > 30 && 'bg-bad-bg',
                )}
              >
                {/* 手机上金额与日期竖排，桌面才拉成一行 —— 固定宽度的列在小屏会溢出 */}
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-1">
                  <button
                    onClick={() => navigate(`/customers/${r.customer.id}`)}
                    className="text-left text-[17px] font-semibold hover:text-brand-700 sm:min-w-40 sm:flex-1"
                  >
                    {r.customer.fullName}
                    <span className="tnum block text-[15px] font-normal text-ink-3">
                      {r.caseNo} · {CASE_TYPE_SHORT[r.type]}
                    </span>
                  </button>

                  {/* 手机：欠款 + 逾期一行，最重要的两个数并排 */}
                  <div className="flex items-baseline gap-3 sm:contents">
                    <span className="tnum text-[21px] font-bold text-warn sm:order-3 sm:w-24 sm:shrink-0 sm:text-right sm:text-[19px]">
                      {rm(r.balanceAmount)}
                    </span>
                    <span
                      className={cx(
                        'tnum text-[16px] font-semibold sm:order-4 sm:w-16 sm:shrink-0 sm:text-right sm:text-[17px]',
                        r.overdueDays > 30 ? 'text-bad' : 'text-warn',
                      )}
                    >
                      逾期 {r.overdueDays} 天
                    </span>
                    <span className="tnum hidden text-[15px] text-ink-2 sm:order-1 sm:block sm:w-28 sm:shrink-0">
                      交付 {fmtDate(r.deliveredAt)}
                    </span>
                    <span className="tnum hidden text-[15px] text-ink-2 sm:order-2 sm:block sm:w-24 sm:shrink-0 sm:text-right">
                      {rm(r.finalAmount)}
                      <span className="block">已收 {rm(r.paidAmount)}</span>
                    </span>
                  </div>

                  {/* 手机上把交付日与已收压成一行小字，不占独立列 */}
                  <p className="tnum text-[15px] text-ink-3 sm:hidden">
                    交付 {fmtDate(r.deliveredAt)} · {rm(r.finalAmount)}，已收 {rm(r.paidAmount)}
                  </p>
                </div>

                {/* 内联操作：减少跳转 */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="tnum text-[15px] text-ink-3">{r.customer.phoneE164}</span>
                  <Button
                    size="sm"
                    icon={<Phone size={15} />}
                    onClick={() => window.open(`tel:${r.customer.phoneE164}`)}
                  >
                    拨打
                  </Button>
                  <Button
                    size="sm"
                    icon={<MessageCircle size={15} />}
                    onClick={() =>
                      window.open(
                        `https://wa.me/${r.customer.phoneE164.replace('+', '')}`,
                        '_blank',
                        'noopener',
                      )
                    }
                  >
                    WhatsApp
                  </Button>
                  <Button size="sm" onClick={() => navigate(`/cases/${r.caseId}`)}>
                    记收款
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

function StatCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: string
  sub?: string
  tone?: 'warn'
}) {
  return (
    <div className="rounded-md border border-line bg-white px-5 py-4 shadow-xs">
      <div className="text-[15px] text-ink-3">{label}</div>
      <div
        className={cx(
          'tnum mt-1 text-[28px] font-bold leading-tight',
          tone === 'warn' && 'text-warn',
        )}
      >
        {value}
      </div>
      {sub && <div className="text-[15px] text-ink-3">{sub}</div>}
    </div>
  )
}
